import React, { useEffect, useState } from "react";
import AnimationPreviewCanvas from "../components/AnimationPreviewCanvas";
import { ANIMATIONS } from "../utils/animations";

export default function AnimationPanel({
  index,
  images,
  clipAnimations,
  animationDuration,
  setAnimationDuration,
  clipAnimationTiming,
  setClipAnimationTiming,
  onChangeAnimation,
  setActiveSection
}) {
  console.log("AnimationPanel render for index:", index);

  // ✅ ALL HOOKS MUST BE AT THE TOP (NO CONDITIONS ABOVE THEM)
  const [loadedImages, setLoadedImages] = useState([]);
  const [localType, setLocalType] = useState(null);

  // ✅ Load images
  useEffect(() => {
    if (!images || !images.length) return;

    const arr = [];
    let loadedCount = 0;

    images.forEach((imgObj) => {
      const img = new Image();
      img.src = imgObj.url;
      img.onload = () => {
        loadedCount++;
        if (loadedCount === images.length) {
          setLoadedImages(arr);
        }
      };
      arr.push(img);
    });
  }, [images]);

  // ✅ Sync localType when index or clipAnimations change
  useEffect(() => {
    if (index !== null && index >= 0) {
      setLocalType(clipAnimations[index] || null);
    }
  }, [index, clipAnimations]);

  // ✅ SAFE EARLY RETURNS AFTER HOOKS
  if (index === null || index < 0) {
    return (
      <div className="p-4 text-gray-500 dark:text-gray-400">
        No clip selected.
      </div>
    );
  }

  const img = loadedImages[index];

  if (!img) {
    return (
      <div className="p-4 text-gray-500 dark:text-gray-400">
        Loading image...
      </div>
    );
  }


  return (
    <div className="
      p-4 h-full overflow-y-auto
      flex flex-col gap-6
      text-gray-900 dark:text-gray-100
    ">
      <h2 className="text-xl font-semibold">Animation</h2>
      <p className="text-sm text-gray-600 dark:text-gray-400">Clip {index + 1}</p>

    {/* ───────── Animation Duration ───────── */}

<div className="flex flex-col gap-2">
  <label className="text-sm text-gray-600 dark:text-gray-400">
    Animation Duration ({animationDuration[index]}s)
  </label>

  <input
    type="range"
    min={0.2}
    max={5}
    step={0.1}
    value={animationDuration[index]}
    onChange={(e) => {
      const v = parseFloat(e.target.value);
      const d = [...animationDuration];
      d[index] = v;
      setAnimationDuration(d);
    }}
    className="w-full accent-purple-500"
  />
</div>


{/* ✅ Timing Mode – Segmented Buttons */}
<div>
  <label className="block mb-2 text-sm text-gray-600 dark:text-gray-400">Play Animation At</label>

  <div className="grid grid-cols-4 gap-2">
    {[
      { key: "full", label: "Full" },
      { key: "start", label: "Start" },
      { key: "end", label: "End" },
      { key: "both", label: "Both" },
    ].map(({ key, label }) => (
      <button
        key={key}
        onClick={() => {
          const t = [...clipAnimationTiming];
          t[index] = key;
          setClipAnimationTiming(t);
        }}
        className={`px-3 py-2 text-xs rounded border transition
          ${
            (clipAnimationTiming[index] || "full") === key
              ?  "bg-purple-600 border-purple-500 text-white"
              : "bg-gray-200 hover:bg-gray-300 text-gray-700 border-gray-300 dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-gray-300 dark:border-gray-700"
          }
        `}
      >
        {label}
      </button>
    ))}
  </div>
</div>


      {/* ───────── Big Preview ───────── */}
        <div className="w-full flex justify-center">
          <div
            className="
              p-2 rounded-lg
              bg-gray-100 dark:bg-gray-900
              border border-gray-200 dark:border-gray-800
            "
          >
            <AnimationPreviewCanvas
              key={localType}
              animation={localType}
              img={img}
            />
          </div>
        </div>

      {/* ───────── Animation Type ───────── */}
        <div className="flex flex-col gap-1">
          <label className="text-sm text-gray-600 dark:text-gray-400">
            Animation Type
          </label>

          <select
            className="
              p-2 rounded-md w-full
              bg-white dark:bg-gray-800
              border border-gray-300 dark:border-gray-700
              text-gray-900 dark:text-gray-100
              focus:outline-none focus:ring-2 focus:ring-purple-500
            "
            value={localType || ""}
            onChange={(e) => {
              const v = e.target.value || null;
              setLocalType(v);
              onChangeAnimation(index, v);
              setActiveSection("images");
            }}
          >
            <option value="">None</option>
            {Object.keys(ANIMATIONS).map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </div>

      {/* ───────── All Animations Grid ───────── */}
    <div>
      <h3 className="text-md font-semibold mb-3">
        All Animations
      </h3>

      <div className="grid grid-cols-2 gap-4">
        {Object.keys(ANIMATIONS).map((a) => {
          const active = localType === a;

          return (
            <button
              key={a}
              onClick={() => {
                setLocalType(a);
                onChangeAnimation(index, a);
                setActiveSection("image");
              }}
              className={`
                p-2 rounded-lg text-left transition-colors
                bg-white dark:bg-gray-900
                border
                ${
                  active
                    ? "border-purple-500"
                    : "border-gray-300 dark:border-gray-700"
                }
                hover:bg-gray-100 dark:hover:bg-gray-800
              `}
            >
              <div className="flex justify-center">
                <AnimationPreviewCanvas animation={a} img={img} />
              </div>

              <div className="mt-1 text-center text-xs text-gray-600 dark:text-gray-300">
                {a}
              </div>
            </button>
          );
        })}
      </div>
    </div>
    </div>
  );
}
